import React, { FC, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './UserActions.module.scss';
import { Button, DotsVertical, DropdownList, Modal } from 'esa-ui-library';
import {
	DELETE_ACCOUNT_MESSAGE,
	DELETE_ACCOUNT_TITLE,
	PERMISSIONS,
	userListActionsActive,
	userListActionsInactive,
} from '../../../..';
import { PROFILE_SETTINGS_URL, history, PropertyType, useStore } from '../../../../../common';
import { AuthContext } from '../../../../../logic';

interface UserActionsProps {
	id: string;
	className?: string;
	dataId?: string;
}

const UserActions: FC<UserActionsProps> = ({ id, className, dataId }): JSX.Element => {
	const [opened, setOpened] = useState(false);
	const [modalOpened, setModalOpened] = useState(false);
	const [{ currentPage, perPage, users }, dispatch] = useStore(false);
	const { user, token } = useContext(AuthContext);
	const canChangeUserDetails = user?.permissions?.isSuperAdmin || user?.permissions[PERMISSIONS.canChangeUserDetails];
	const currentUserIndex = users.findIndex((u: PropertyType) => u.user_id === id);
	const shouldOpenUp = users.length >= 4 && currentUserIndex >= 0 && users.length - currentUserIndex <= 2;

	const isUserActive = () => {
		const currentUser = users.find((u: PropertyType) => u.user_id === id);
		return parseInt(currentUser.state);
	};

	const onUserEdit = (id: string) => {
		history.push(`${PROFILE_SETTINGS_URL.value}/${id}`);
	};

	const onDropdownOpen = () => {
		setOpened(true);
	};

	const onDropdownClose = () => {
		setOpened(false);
	};

	const onModalClose = () => {
		setModalOpened(false);
	};

	const onOpenReportModal = () => {
		dispatch('REPORTS_MODAL_OPEN', id);
	};

	const onAccountDelete = async () => {
		await dispatch('DELETE_ACCOUNT', { token, id });
		dispatch('PAGE_UPDATE', { token, page: currentPage, perPage });
		onModalClose();
	};

	const onAccountActivate = async () => {
		await dispatch('ACTIVATE_ACCOUNT', { token, id });
		dispatch('PAGE_UPDATE', { token, page: currentPage, perPage });
	};

	const onAccountDeactivate = async () => {
		await dispatch('DEACTIVATE_ACCOUNT', { token, id });
		dispatch('PAGE_UPDATE', { token, page: currentPage, perPage });
	};

	const onAction = (action: PropertyType) => {
		if (action.value === 'activate') return onAccountActivate();
		if (action.value === 'deactivate') return onAccountDeactivate();
		if (action.value === 'report') return onOpenReportModal();
		return setModalOpened(true);
	};

	const getDropdownOptions = () => {
		const actions = isUserActive() ? userListActionsActive : userListActionsInactive;
		const activeActions = [];

		(user?.permissions?.isSuperAdmin || user?.permissions[PERMISSIONS.canDeleteUser]) && activeActions.push(actions[0]);
		(user?.permissions?.isSuperAdmin || user?.permissions[PERMISSIONS.canDisableUser]) &&
			activeActions.push(actions[1]);
		if (user?.permissions?.isSuperAdmin && isUserActive()) {
			activeActions.push(actions[2]);
		}
		return activeActions;
	};

	return (
		<section className={`${className ? className : ''}`} data-testid={dataId}>
			<Button title={`${canChangeUserDetails ? 'Edit' : 'View'}`} small onClick={() => onUserEdit(id)} />
			{canChangeUserDetails && (
				<Button className={styles.DropdownButton} small onClick={onDropdownOpen} dataId='dropdown-button'>
					<>
						<DotsVertical />
						{opened && (
							<DropdownList
								options={getDropdownOptions()}
								className={`${styles.ActionDropdown} ${shouldOpenUp ? styles.OpenUp : ''}`}
								onSelect={onAction}
								onClose={onDropdownClose}
							/>
						)}
					</>
				</Button>
			)}
			{modalOpened && (
				<Modal
					danger
					title={DELETE_ACCOUNT_TITLE}
					submitLabel='Delete Account'
					onCancel={onModalClose}
					onSubmit={onAccountDelete}
				>
					{DELETE_ACCOUNT_MESSAGE}
				</Modal>
			)}
		</section>
	);
};

UserActions.propTypes = {
	id: PropTypes.string.isRequired,
	className: PropTypes.string,
	dataId: PropTypes.string,
};

export { UserActions };
